import { Injectable } from '@angular/core'
import { JudgeRole } from '@ftr/annotations-contracts'
import { AnnotationsApiClient, AnnotationsApiClientFactory } from '@ftr/api-annotations'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { RemoteData } from '@ftr/foundation'
import { Action, createSelector, Selector, State, StateContext, StateOperator } from '@ngxs/store'
import { patch } from '@ngxs/store/operators'
import { StatusCodes } from 'http-status-codes'
import { memoize } from 'lodash-es'
import { firstValueFrom, Observable, of } from 'rxjs'
import {
  AddJudgeRoleCommand,
  DeleteJudgeRoleCommand,
  FetchJudgeRolesListPageCommand,
  FetchNextJudgeRolesListPageCommand,
  InitialiseJudgeRolesListCommand,
  UpdateJudgeRoleCommand,
  UpdateJudgeRolesListCommand,
} from './judge-roles.commands'
import { JudgeRolesListAccessForbidden } from './judge-roles.event'
import { JudgeRolesListInstanceStateModel, JudgeRolesListStateModel, JudgeRoleStatus } from './judge-roles.model'

export const JUDGE_ROLES_PAGE_SIZE = 100

export function defaultJudgeRolesListState(): JudgeRolesListStateModel {
  return { instances: {} }
}

@State<JudgeRolesListStateModel>({
  name: 'judgeRolesState',
  defaults: defaultJudgeRolesListState(),
})
@Injectable()
export class JudgeRolesState {
  constructor(
    private readonly regionCache: CourtSystemRegionCache,
    private readonly annotationsApiFactory: AnnotationsApiClientFactory,
  ) {}

  @Selector()
  static allInstanceStates(state: JudgeRolesListStateModel): Record<Uuid, JudgeRolesListInstanceStateModel> {
    return state?.instances ?? {}
  }

  static readonly judgeRoles = memoize(
    (courtSystemId: string): ((sourceStates: ReturnType<typeof JudgeRolesState.allInstanceStates>) => JudgeRole[]) =>
      createSelector([JudgeRolesState.allInstanceStates], sourceStates => {
        return sourceStates[courtSystemId]?.judgeRoles ?? []
      }),
    courtSystemId => courtSystemId,
  )

  static readonly pageResult = memoize(
    (
      courtSystemId: string,
    ): ((
      sourceStates: ReturnType<typeof JudgeRolesState.allInstanceStates>,
    ) => JudgeRolesListInstanceStateModel['pageResultRemote']) =>
      createSelector([JudgeRolesState.allInstanceStates], sourceStates => {
        return sourceStates[courtSystemId]?.pageResultRemote
      }),
    courtSystemId => courtSystemId,
  )

  static readonly status = memoize(
    (
      courtSystemId: string,
    ): ((sourceStates: ReturnType<typeof JudgeRolesState.allInstanceStates>) => JudgeRoleStatus) =>
      createSelector([JudgeRolesState.allInstanceStates], sourceStates => {
        return sourceStates[courtSystemId]?.status ?? []
      }),
    courtSystemId => courtSystemId,
  )

  @Action(InitialiseJudgeRolesListCommand)
  private initialiseJudgeRolesList(
    { setState, getState, dispatch }: StateContext<JudgeRolesListStateModel>,
    { courtSystemId }: InitialiseJudgeRolesListCommand,
  ): Observable<void> | undefined {
    const instance = getState().instances[courtSystemId]
    if (instance?.pageResultRemote.isSuccess() || instance?.pageResultRemote.isLoading()) {
      return
    }

    setState(
      makeInstancePatch(courtSystemId, {
        judgeRoles: [],
        pageResultRemote: RemoteData.notAsked(),
      }),
    )

    return dispatch(new FetchJudgeRolesListPageCommand(courtSystemId))
  }

  @Action(UpdateJudgeRolesListCommand)
  private async updateJudgeRolesList(
    { setState, dispatch }: StateContext<JudgeRolesListStateModel>,
    { courtSystemId, judgeRoles }: UpdateJudgeRolesListCommand,
  ): Promise<void> {
    const updatedJudgeRoles: JudgeRole[] = judgeRoles.map((role, index) => ({ ...role, sequenceNumber: index + 1 }))
    setState(makeInstancePatch(courtSystemId, { judgeRoles: updatedJudgeRoles }))
    const apiClient = await this.getApiClient(courtSystemId)

    setState(
      makeInstancePatch(courtSystemId, {
        status: { type: 'saving' },
      }),
    )

    try {
      const result = await apiClient.judgeRoles.orderJudgeRoles({ body: updatedJudgeRoles })
      if (result.status === 200) {
        setState(
          makeInstancePatch(courtSystemId, {
            status: { type: 'updated' },
          }),
        )

        dispatch(new FetchJudgeRolesListPageCommand(courtSystemId))
      } else {
        setState(
          makeInstancePatch(courtSystemId, {
            pageResultRemote: RemoteData.failure(new Error('Failed to update judge roles', { cause: result.body })),
            status: { type: 'loaded' },
          }),
        )
      }
    } catch (err) {
      setState(
        makeInstancePatch(courtSystemId, {
          pageResultRemote: RemoteData.failure(new Error('Failed to update judge roles', { cause: err })),
          status: { type: 'loaded' },
        }),
      )
    }
  }

  @Action(FetchNextJudgeRolesListPageCommand)
  private fetchNextJudgeRolesPage(
    { getState, dispatch }: StateContext<JudgeRolesListStateModel>,
    { courtSystemId }: FetchNextJudgeRolesListPageCommand,
  ): Observable<void> | void {
    const { pageResultRemote, judgeRoles } = getState().instances[courtSystemId]
    const offset = judgeRoles.length

    if (pageResultRemote.get()?.hasMore) {
      return dispatch(new FetchJudgeRolesListPageCommand(courtSystemId, offset, JUDGE_ROLES_PAGE_SIZE))
    }
  }

  @Action(FetchJudgeRolesListPageCommand)
  private async fetchJudgeRolesPage(
    { getState, setState, dispatch }: StateContext<JudgeRolesListStateModel>,
    { courtSystemId, offset = 0, limit = JUDGE_ROLES_PAGE_SIZE }: FetchJudgeRolesListPageCommand,
  ): Promise<void> {
    const { pageResultRemote, judgeRoles } = getState().instances[courtSystemId]
    if (pageResultRemote.isLoading()) {
      return
    }
    setState(
      makeInstancePatch(courtSystemId, {
        pageResultRemote: RemoteData.loading(),
      }),
    )

    const apiClient = await this.getApiClient(courtSystemId)

    try {
      const result = await apiClient.judgeRoles.listJudgeRoles({ query: { courtSystemId, offset, limit } })
      if (result.status === 200) {
        setState(
          makeInstancePatch(courtSystemId, {
            judgeRoles: offset === 0 ? result.body.items : judgeRoles.concat(result.body.items),
            pageResultRemote: RemoteData.success({ hasMore: result.body.items.length === limit }),
            status: { type: 'loaded' },
          }),
        )
      } else {
        setState(
          makeInstancePatch(courtSystemId, {
            judgeRoles: [],
            pageResultRemote: RemoteData.failure(new Error('Failed to fetch judge roles', { cause: result.body })),
            status: { type: 'loaded' },
          }),
        )
        if (result.status === 403) {
          dispatch(new JudgeRolesListAccessForbidden(courtSystemId))
        }
      }
    } catch (err) {
      setState(
        makeInstancePatch(courtSystemId, {
          judgeRoles: [],
          pageResultRemote: RemoteData.failure(new Error('Failed to fetch judge roles', { cause: err })),
          status: { type: 'loaded' },
        }),
      )
    }
  }

  @Action(AddJudgeRoleCommand)
  private async addJudgeRole(
    { dispatch, setState }: StateContext<JudgeRolesListStateModel>,
    { courtSystemId, roleName }: AddJudgeRoleCommand,
  ): Promise<void> {
    const apiClient = await this.getApiClient(courtSystemId)

    setState(
      makeInstancePatch(courtSystemId, {
        status: { type: 'saving' },
      }),
    )

    const result = await apiClient.judgeRoles.createJudgeRole({ body: { courtSystemId, name: roleName } })
    if (result.status === 201) {
      setState(
        makeInstancePatch(courtSystemId, {
          status: { type: 'saved' },
        }),
      )
      dispatch(new FetchJudgeRolesListPageCommand(courtSystemId))
      return
    }
    const errorMessage = result.status === 409 ? 'This role already exists.' : 'An error occurred. Please try again.'
    setState(
      makeInstancePatch(courtSystemId, {
        status: { type: 'failed', error: new Error(errorMessage) },
      }),
    )
  }

  @Action(UpdateJudgeRoleCommand)
  private async updateJudgeRole(
    { dispatch, setState }: StateContext<JudgeRolesListStateModel>,
    { courtSystemId, role }: UpdateJudgeRoleCommand,
  ): Promise<void> {
    const apiClient = await this.getApiClient(courtSystemId)

    setState(
      makeInstancePatch(courtSystemId, {
        status: { type: 'saving' },
      }),
    )

    const result = await apiClient.judgeRoles.updateJudgeRole({ body: { ...role, name: role.name } })
    if (result.status === 200) {
      setState(
        makeInstancePatch(courtSystemId, {
          status: { type: 'updated' },
        }),
      )
      dispatch(new FetchJudgeRolesListPageCommand(courtSystemId))
      return
    }
    const errorMessage =
      result.status === 409 ? 'A role with this name already exists.' : 'An error occurred. Please try again.'
    setState(
      makeInstancePatch(courtSystemId, {
        status: { type: 'failed', error: new Error(errorMessage) },
      }),
    )
  }

  @Action(DeleteJudgeRoleCommand)
  private async deleteJudgeRole(
    { dispatch, setState }: StateContext<JudgeRolesListStateModel>,
    { courtSystemId, roleId }: DeleteJudgeRoleCommand,
  ): Promise<void> {
    const region = (await firstValueFrom(this.regionCache.getOrFetchCourtSystem(of(courtSystemId)))).region
    const apiClient = this.annotationsApiFactory.build(region)
    setState(
      makeInstancePatch(courtSystemId, {
        status: { type: 'deleting' },
      }),
    )

    const result = await apiClient.judgeRoles.deleteJudgeRole({ body: { courtSystemId, id: roleId } })
    if (result.status === StatusCodes.NO_CONTENT) {
      setState(
        makeInstancePatch(courtSystemId, {
          status: { type: 'deleted' },
        }),
      )
      dispatch(new FetchJudgeRolesListPageCommand(courtSystemId))
    } else {
      const errorMessage = 'An error occurred. Please try again.'
      setState(
        makeInstancePatch(courtSystemId, {
          status: { type: 'delete-failed', error: new Error(errorMessage) },
        }),
      )
    }
  }

  private async getApiClient(courtSystemId: Uuid): Promise<AnnotationsApiClient> {
    const region = (await firstValueFrom(this.regionCache.getOrFetchCourtSystem(of(courtSystemId)))).region
    return this.annotationsApiFactory.build(region)
  }
}

function makeInstancePatch(
  instanceId: string,
  instancePartial: Partial<JudgeRolesListInstanceStateModel>,
): StateOperator<JudgeRolesListStateModel> {
  return patch({
    instances: patch({
      [instanceId]: patch(instancePartial),
    }),
  })
}
